import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Control, Room } from '@guest/shared';
import { Icon } from '../lib/icons.js';
import { useHouse } from '../lib/store.js';
import { greeting, timeLeft, formatDateTime } from '../lib/format.js';
import { AlarmCard } from '../components/AlarmCard.js';
import { DoorCard } from '../components/DoorCard.js';
import { ConnectionPill } from '../components/Chrome.js';
import { PrimaryToggle } from '../components/controls.js';
import { isOn } from '../lib/entity.js';

export const primaryControls = (room: Room, zone?: string): Control[] =>
  room.controls.filter((c) => c.primary === true && (zone === undefined || (c.zone ?? '') === zone));

export function RoomCard({ room, lit }: { room: Room; lit: boolean }): ReactNode {
  const mains = primaryControls(room);
  const counts = room.controls.reduce<Record<string, number>>((acc, c) => {
    acc[c.group] = (acc[c.group] ?? 0) + 1;
    return acc;
  }, {});
  const summary = [
    counts['lighting'] ? `${counts['lighting']} lights` : null,
    counts['climate'] ? 'climate' : null,
    counts['media'] ? 'music' : null,
    counts['covers'] ? 'blinds' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // The whole card navigates via a stretched link, leaving the toggle above it
  // as a real button — nesting a switch inside an <a> would break both.
  return (
    <div className="glass room-card" data-lit={lit}>
      <div className="top">
        <span className="glyph">
          <Icon name={room.icon} size={19} />
        </span>
        {mains.length > 0 && (
          <span className="card-action">
            <PrimaryToggle controls={mains} label={`${room.name} main light`} />
          </span>
        )}
      </div>
      <div>
        <Link to={`/room/${room.id}`} className="stretched">
          <span className="name">{room.name}</span>
        </Link>
        <div className="meta">
          {summary || room.blurb || 'View'}
          <Icon name="chevron" size={13} />
        </div>
      </div>
    </div>
  );
}

export function Home(): ReactNode {
  const { context, states } = useHouse();
  if (!context) return <div className="empty">Loading the house…</div>;

  const guestRoom = context.rooms.find((r) => r.id === context.guestRoomId);
  const otherRooms = context.rooms.filter((r) => r.id !== context.guestRoomId);
  const roomIsLit = (room: Room): boolean =>
    room.controls.some((c) => c.group === 'lighting' && isOn(states.get(c.entityId)));

  // A guest room is split bedroom/ensuite, so each zone gets its own main light.
  const guestZones = guestRoom
    ? [...new Set(guestRoom.controls.map((c) => c.zone ?? ''))]
        .map((zone) => ({ zone, mains: primaryControls(guestRoom, zone) }))
        .filter((z) => z.mains.length > 0)
    : [];

  return (
    <main className="shell">
      <div className="greeting">
        <h2>
          {greeting()},<br />
          {context.guestName.split(' ')[0]}.
        </h2>
        <p>
          {context.houseName} · {timeLeft(context.endsAt)}
        </p>
      </div>

      <ConnectionPill />

      {context.scheduleScope !== 'none' && (
        <div className="glass in-control">
          <Icon name={context.scheduleScope === 'house' ? 'home' : 'bed'} size={16} />
          <span>
            {context.scheduleScope === 'house'
              ? 'You are looking after the house.'
              : 'Your stay is active.'}
          </span>
        </div>
      )}

      {context.scheduleCount > 0 && (
        <Link to="/timings" className="glass timings-card">
          <span className="glyph">
            <Icon name="clock" size={20} />
          </span>
          <span className="body">
            <span className="t">Lighting times</span>
            <span className="s">
              {context.scheduleScope === 'house'
                ? 'When your room wakes up, and when the house dims for the evening'
                : 'When your room lights itself in the morning and stops at night'}
            </span>
          </span>
          <Icon name="chevron" size={16} />
        </Link>
      )}

      <div className="hero-grid">
        {context.alarmEntityId && (
          <AlarmCard entityId={context.alarmEntityId} globals={context.globals} />
        )}
        {context.frontDoorLockEntityId && (
          <DoorCard
            entityId={context.frontDoorLockEntityId}
            contactEntityId={context.frontDoorContactEntityId}
          />
        )}
      </div>

      {guestRoom && (
        <>
          <div className="floor-label">
            <span className="eyebrow">Your room</span>
          </div>
          <div className="hero-grid">
            <section className="glass hero">
              <div className="hero-top">
                <span className="hero-glyph" data-tone={roomIsLit(guestRoom) ? 'caution' : undefined}>
                  <Icon name={guestRoom.icon} size={22} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div className="hero-title">{guestRoom.name}</div>
                  <div className="hero-sub">{guestRoom.blurb ?? 'Your space'}</div>
                </div>
                <span className="spacer" />
                <Link to={`/room/${guestRoom.id}`} className="icon-btn" aria-label="Open your room">
                  <Icon name="chevron" size={16} />
                </Link>
              </div>

              {guestZones.map(({ zone, mains }) => (
                <div className="light-row" key={zone || 'all'}>
                  <Icon name="bulb" size={17} style={{ color: 'var(--text-faint)' }} />
                  <span>{zone ? `${zone} light` : 'Main light'}</span>
                  <span className="spacer" />
                  <PrimaryToggle controls={mains} label={`${zone || guestRoom.name} main light`} />
                </div>
              ))}
            </section>
          </div>
        </>
      )}

      {otherRooms.length > 0 && (
        <>
          <div className="floor-label">
            <span className="eyebrow">The rest of the house</span>
          </div>
          <div className="room-grid">
            {otherRooms.map((room) => (
              <RoomCard key={room.id} room={room} lit={roomIsLit(room)} />
            ))}
          </div>
        </>
      )}

      <p style={{ marginTop: 'var(--space-6)', fontSize: 12.5, color: 'var(--text-faint)' }}>
        Access from {formatDateTime(context.startsAt)} until {formatDateTime(context.endsAt)}.
      </p>
    </main>
  );
}
